// PostCard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Observer from '@researchgate/react-intersection-observer';

import Loading from '../component/Loading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage from '../component/LazyImage.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';

import TrailerBlocker from '../container/TrailerBlocker.js';

import { color } from '../style/variables.js';
import media from '../style/media.js';

import { getKeySystem } from '../resource/getDrmInfo.js';

import VideoIconSource from '../../img/ic_grid_video.svg';
import MultipleIconSource from '../../img/btn_post_24.svg';

export class PostCard extends React.PureComponent {
  state = {
    isIntersecting: false,
    isServer: true,
  };
  handleIntersection = ({ isIntersecting }, unobserve) => {
    this.setState({ isIntersecting });
    if (isIntersecting) {
      unobserve();
    }
  };
  fetchMessageDetail = () => {
    const { postId, firstAssetContentType, fetchMessageDetail } = this.props;
    const { isIntersecting } = this.state;
    if ((postId && !firstAssetContentType) || isIntersecting) {
      return fetchMessageDetail({
        postId,
      });
    }
    return;
  };
  fetchDrmLicenseAvailability = () => {
    const { postId, canViewMedia, fetchDrmLicenseAvailability } = this.props;
    const { isIntersecting } = this.state;
    if (!postId || canViewMedia !== null || !isIntersecting) {
      return;
    }
    return fetchDrmLicenseAvailability({
      messageId: postId,
      keySystem: getKeySystem(),
    });
  };
  componentDidMount() {
    this.fetchMessageDetail();
    this.fetchDrmLicenseAvailability();
    this.nextTick = setTimeout(() => {
      this.setState({
        isServer: false,
      });
    }, 0);
  }
  componentDidUpdate(prevProps, prevState) {
    const { postId, firstAssetContentType, canViewMedia } = this.props;
    const { isIntersecting } = this.state;
    if (
      postId !== prevProps.postId ||
      canViewMedia !== prevProps.canViewMedia ||
      isIntersecting !== prevState.isIntersecting
    ) {
      this.fetchDrmLicenseAvailability();
    }
    if (
      postId !== prevProps.postId ||
      firstAssetContentType !== prevProps.firstAssetContentType ||
      isIntersecting !== prevState.isIntersecting
    ) {
      this.fetchMessageDetail();
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }
  render() {
    const {
      postId,
      firstAssetContentType,
      jpg1x,
      jpg2x,
      webp1x,
      webp2x,
      assetIds,
      onImageLoad,
    } = this.props;
    const { isServer, isIntersecting } = this.state;
    const isMultiple = assetIds.length > 1;
    const isVideo = firstAssetContentType.startsWith('video/');
    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    if (!postId) {
      return <StyledPostCard />;
    }
    return (
      <Observer onChange={this.handleIntersection} threshold={0.1}>
        <StyledPostCard>
          {shouldHydrate ? (
            <HydrationBoundary
              wrapper={<StyledPostCard />}
              shouldHydrate={isIntersecting}
            >
              <Picture
                data-key="target"
                srcSet={`${jpg1x} 1x, ${jpg2x} 2x`}
                src={jpg1x}
              >
                <source srcSet={`${webp1x} 1x, ${webp2x} 2x`} />
                <img src={jpg1x} srcSet={`${jpg1x} 1x, ${jpg2x} 2x`} />
              </Picture>
            </HydrationBoundary>
          ) : (
            <StatefulImage>
              <Loading data-key="error" />
              <Loading data-key="loading" />
              <LazyImage
                data-key="target"
                srcSet={`${jpg1x} 1x, ${jpg2x} 2x`}
                src={jpg1x}
                threshold={0.25}
                onLoad={onImageLoad}
              >
                <source srcSet={`${webp1x} 1x, ${webp2x} 2x`} />
              </LazyImage>
            </StatefulImage>
          )}
          {isMultiple ? (
            <img src={MultipleIconSource} />
          ) : isVideo ? (
            <img src={VideoIconSource} />
          ) : null}
          <TrailerBlocker enlarge={30} />
        </StyledPostCard>
      </Observer>
    );
  }
}

PostCard.propTypes = {
  postId: PropTypes.string,
  firstAssetContentType: PropTypes.string,
  jpg1x: PropTypes.string,
  jpg2x: PropTypes.string,
  webp1x: PropTypes.string,
  webp2x: PropTypes.string,
  canViewMedia: PropTypes.bool,
  assetIds: PropTypes.array,
  fetchMessageDetail: PropTypes.func,
  fetchDrmLicenseAvailability: PropTypes.func,
  onImageLoad: PropTypes.func,
};

PostCard.defaultProps = {
  postId: null,
  firstAssetContentType: '',
  jpg1x: null,
  jpg2x: null,
  webp1x: null,
  webp2x: null,
  canViewMedia: null,
  assetIds: [],
  fetchMessageDetail: () => null,
  fetchDrmLicenseAvailability: () => null,
  onImageLoad: () => null,
};

const StyledPostCard = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${color.grey['300']};
  overflow: hidden;
  > img {
    position: absolute;
    z-index: 1;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
  ${media.tablet`
    > img {
      width: 24px;
      height: 24px;
    }
  `};
  ${media.mobile`
    > img {
      top: 4px;
      right: 4px;
      width: 16px;
      height: 16px;
    }
  `};
`;

const Picture = styled.picture`
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export default PostCard;
